import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi, EmptyState } from "@/components/common";
import { DonateDialog } from "@/components/zakat/DonateDialog";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtUSDStrict, timeAgo } from "@/lib/format";
import {
  Moon, HandCoins, Scale, Sparkles, FileDown, CalendarClock, Droplets,
} from "lucide-react";

const pct = (v) => `${Number(v || 0).toFixed(2)}%`;

// The backend labels these lines in English; give them an Arabic face.
const LINE_KEYS = {
  "Cash in your wallets": "zk_l_cash", "USD savings": "zk_l_savings",
  "Murabaha placements": "zk_l_murabaha", "Shares & ETFs": "zk_l_shares",
};
const NOTE_KEYS = {
  "Cash in your wallets": "zk_n_cash", "USD savings": "zk_n_savings",
  "Murabaha placements": "zk_n_murabaha", "Shares & ETFs": "zk_n_shares",
};

export default function Zakat() {
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [charities, setCharities] = useState([]);
  const [history, setHistory] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [anniv, setAnniv] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, c, h] = await Promise.all([
        api.get("/zakat/summary"), api.get("/zakat/charities"), api.get("/zakat/donations"),
      ]);
      setData(s.data); setCharities(c.data.charities); setHistory(h.data.donations);
      setAnniv((s.data.cycle.cycle_start || "").slice(0, 10));
    } catch (e) {
      console.error("Failed to load zakat summary:", e);
      toast.error("Couldn't load your zakat figures", { description: "Please refresh to try again." });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveAnniv = async () => {
    try {
      await api.post("/zakat/anniversary", { date: anniv });
      toast.success("Zakat year updated", { description: "We'll count your lunar year from there." });
      load();
    } catch (e) {
      toast.error("Couldn't set that date", { description: e?.response?.data?.detail });
    }
  };

  if (!data) return <AppShell><div className="p-8 text-sm text-muted-foreground">Loading...</div></AppShell>;

  const due = new Date(data.cycle.due_date);
  return (
    <AppShell>
      <div data-testid="zakat-page">
        <PageHeader title={t("zk_title")}
          subtitle={t("zk_sub")}
          testid="zakat-header"
          action={
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setDialog("zakat")} data-testid="zakat-give-button">
              <HandCoins className="h-4 w-4 me-1.5" />{t("zk_give_zakat")}
            </Button>
          } />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Kpi label={t("zk_outstanding")} value={fmtUSDStrict(data.zakat_outstanding_usd)}
            icon={Moon} tint={0} accent testid="zakat-kpi-outstanding" />
          <Kpi label={t("zk_wealth")} value={fmtUSDStrict(data.assets.zakatable_usd)}
            icon={Scale} tint={1} testid="zakat-kpi-wealth" />
          <Kpi label={t("zk_purification")} value={fmtUSDStrict(data.purification_outstanding_usd)}
            icon={Droplets} tint={2} testid="zakat-kpi-purification" />
          <Kpi label={t("zk_days")} value={data.cycle.days_remaining}
            icon={CalendarClock} tint={3} testid="zakat-kpi-days" />
        </div>

        <div className="grid gap-4 sm:gap-5 lg:grid-cols-3 mt-4 sm:mt-5">
          <Card className="p-5 shadow-premium lg:col-span-2" data-testid="zakat-breakdown-card">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Scale className="h-4 w-4" />{t("zk_how")}
            </div>
            <div className="mt-3 rounded-xl border divide-y">
              {data.assets.lines.map((l) => (
                <div key={l.label} className="flex items-start justify-between gap-4 px-3.5 py-3">
                  <div>
                    <div className="text-sm font-medium">{LINE_KEYS[l.label] ? t(LINE_KEYS[l.label]) : l.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{NOTE_KEYS[l.label] ? t(NOTE_KEYS[l.label]) : l.note}</div>
                  </div>
                  <span className="numeric text-sm font-semibold shrink-0">{fmtUSDStrict(l.usd)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3.5 py-3 bg-secondary">
                <span className="text-sm font-semibold">{t("zk_l_total")}</span>
                <span className="numeric text-sm font-bold" data-testid="zakat-total">
                  {fmtUSDStrict(data.assets.zakatable_usd)}
                </span>
              </div>
              <div className="flex items-center justify-between px-3.5 py-3">
                <span className="text-sm text-muted-foreground">{t("zk_l_rate")}</span>
                <span className="numeric text-sm font-semibold">{fmtUSDStrict(data.zakat_due_usd)}</span>
              </div>
              {data.zakat_given_usd > 0 && (
                <div className="flex items-center justify-between px-3.5 py-3">
                  <span className="text-sm text-muted-foreground">{t("zk_l_paid")}</span>
                  <span className="numeric text-sm font-semibold text-emerald-600">
                    −{fmtUSDStrict(data.zakat_given_usd)}
                  </span>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">{data.explainer}</p>
          </Card>

          <div className="space-y-4 sm:space-y-5">
            <Card className="p-5 shadow-premium" data-testid="zakat-nisab-card">
              <div className="text-sm font-semibold">{t("zk_nisab")}</div>
              <div className="numeric text-2xl font-bold mt-1.5">{fmtUSDStrict(data.nisab.usd)}</div>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">
                {data.nisab.grams}g of gold at {fmtUSDStrict(data.nisab.gold_price_usd_oz)} per troy
                ounce. {data.above_nisab
                  ? "Your wealth is above Nisab, so zakat is due."
                  : "Your wealth is below Nisab, so no zakat is due this year."}
              </p>
              <div className={`mt-3 inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.08em] ${
                data.above_nisab ? "bg-[color:var(--tharwy-lime)]/15 text-[color:var(--tharwy-lime)]"
                                 : "bg-muted text-muted-foreground"}`}
                data-testid="zakat-nisab-state">
                {data.above_nisab ? "Above nisab" : "Below nisab"}
              </div>
            </Card>

            <Card className="p-5 shadow-premium" data-testid="zakat-anniversary-card">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CalendarClock className="h-4 w-4" />{t("zk_your_year")}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Next due {due.toLocaleDateString()} · {data.cycle.days_remaining} days away.
                A lunar year is 354 days.
              </p>
              <div className="mt-3 space-y-1.5">
                <Label className="text-xs">Counted from</Label>
                <div className="flex gap-2">
                  <Input type="date" value={anniv} onChange={(e) => setAnniv(e.target.value)}
                    data-testid="zakat-anniversary-input" />
                  <Button variant="outline" onClick={saveAnniv} data-testid="zakat-anniversary-save">
                    Save
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="zakat-purification-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Droplets className="h-4 w-4" />Purification owed
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {data.purification.formula} — from the AAOIFI screening on your own holdings.
              </p>
            </div>
            <Button variant="outline" onClick={() => setDialog("purification")}
              disabled={data.purification_outstanding_usd <= 0}
              data-testid="zakat-purify-button">
              <Sparkles className="h-4 w-4 me-1.5" />
              Give {fmtUSDStrict(data.purification_outstanding_usd)}
            </Button>
          </div>

          {data.purification.rows.length ? (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-start font-semibold py-2">Holding</th>
                    <th className="text-end font-semibold py-2">Value</th>
                    <th className="text-end font-semibold py-2">Dividend yield</th>
                    <th className="text-end font-semibold py-2">Non-permissible</th>
                    <th className="text-end font-semibold py-2">Owed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.purification.rows.map((r) => (
                    <tr key={r.symbol} className="border-t" data-testid={`purify-row-${r.symbol}`}>
                      <td className="py-2">
                        <div className="font-medium">{r.symbol}</div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.name}</div>
                      </td>
                      <td className="text-end numeric">{fmtUSDStrict(r.value_usd)}</td>
                      <td className="text-end numeric text-muted-foreground">{pct(r.dividend_yield_pct)}</td>
                      <td className="text-end numeric text-muted-foreground">{pct(r.purification_pct)}</td>
                      <td className="text-end numeric font-semibold">{fmtUSDStrict(r.owed_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="Nothing to purify"
              hint="None of your holdings are paying dividends traced to non-permissible income."
              testid="zakat-purify-empty" />
          )}
        </Card>

        <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="zakat-history-card">
          <div className="text-sm font-semibold">{t("zk_given")}</div>
          {history.length ? (
            <div className="mt-3 rounded-xl border divide-y">
              {history.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 px-3.5 py-3"
                  data-testid={`zakat-donation-${d.reference}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{d.charity_name}</div>
                    <div className="text-[11px] text-muted-foreground numeric">
                      {d.kind === "zakat" ? "Zakat" : "Purification"} · {d.reference} · {timeAgo(d.timestamp)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="numeric text-sm font-semibold">{fmtUSDStrict(d.amount_usd)}</span>
                    <Button variant="ghost" size="sm"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/zakat/donations/${d.id}/pdf`, "_blank")}
                      data-testid={`zakat-certificate-${d.reference}`}>
                      <FileDown className="h-4 w-4 me-1.5" />{t("zk_certificate")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Nothing given yet"
              hint="Every zakat and purification payment you make will be listed here with a downloadable certificate."
              testid="zakat-history-empty" />
          )}
        </Card>

        <DonateDialog open={!!dialog} onOpenChange={(v) => setDialog(v ? dialog : null)}
          kind={dialog || "zakat"} charities={charities} available={data.usd_available}
          suggested={dialog === "purification" ? data.purification_outstanding_usd
                                              : data.zakat_outstanding_usd}
          onDone={load} />
      </div>
    </AppShell>
  );
}
